import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  forwardRef,
  input,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ButtonComponent } from 'src/app/ui-kit/button/button.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/** Reason a file was rejected by the validator. */
export type FileRejectionReason = 'type' | 'size' | 'count';

/**
 *
 */
export interface FileUploadRejection {
  file: File;
  reason: FileRejectionReason;
  message: string;
}

/**
 * Dropzone + file picker with client-side validation. CVA-compliant so it works in
 * reactive forms (the control value is a `File[]`). Does **not** upload — the caller
 * handles the bytes.
 *
 * @example
 * <app-file-upload
 *   accept="image/*,application/pdf"
 *   [multiple]="true"
 *   [maxFiles]="5"
 *   [maxSizeBytes]="5_000_000"
 *   (filesChange)="onFiles($event)"
 *   (rejected)="onRejected($event)"
 * />
 *
 * @feature shared
 */
@Component({
  selector: 'app-file-upload',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, ButtonComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
  host: { class: 'block' },
  template: `
    <div
      class="rounded-xl border-2 border-dashed border-outline bg-surface-low p-6 flex flex-col items-center gap-3 text-center transition-colors focus-within:border-outline-high"
      [class.border-primary]="isDragOver()"
      [class.bg-surface-hover]="isDragOver()"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave()"
      (drop)="onDrop($event)"
      role="button"
      tabindex="0"
      [attr.aria-label]="ariaLabel() ?? 'Upload files'"
      (click)="openPicker()"
      (keydown.enter)="openPicker()"
      (keydown.space)="openPicker(); $event.preventDefault()"
    >
      <app-icon name="cloud-upload" size="2xl" color="medium" />
      <div>
        <p class="font-semibold text-body">Drop files here or click to browse</p>
        @if (hint()) {
          <p class="text-sm text-muted">{{ hint() }}</p>
        }
      </div>
      <app-button color="medium" fill="outline" size="small" (click)="openPicker(); $event.stopPropagation()">
        Choose files
      </app-button>
      <input
        #picker
        type="file"
        class="hidden"
        [accept]="accept()"
        [multiple]="multiple()"
        (change)="onPicked($event)"
      />
    </div>

    @if (files().length > 0) {
      <ul class="mt-3 flex flex-col gap-2">
        @for (file of files(); track file.name + '_' + file.lastModified) {
          <li class="flex items-center justify-between gap-3 rounded border border-outline bg-surface px-3 py-2">
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-semibold text-body">{{ file.name }}</p>
              <p class="text-xs text-muted">{{ formatSize(file.size) }}</p>
            </div>
            <button
              type="button"
              class="rounded-full p-1 text-muted hover:text-body focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
              [attr.aria-label]="'Remove ' + file.name"
              (click)="remove(file)"
            >
              <app-icon name="close" size="xs" />
            </button>
          </li>
        }
      </ul>
    }
  `,
})
export class FileUploadComponent implements ControlValueAccessor {
  /** MIME types / extensions accepted. Passed to the native input's `accept`. */
  readonly accept = input<string>('');

  /** Allow multiple file selection. @default false */
  readonly multiple = input<boolean>(false);

  /** Max number of files. Ignored when `multiple` is false. @default 10 */
  readonly maxFiles = input<number>(10);

  /** Max per-file size in bytes. @default 10 MB */
  readonly maxSizeBytes = input<number>(10 * 1024 * 1024);

  /** Helper text shown under the headline. */
  readonly hint = input<string | undefined>(undefined);

  /** Accessible label override. */
  readonly ariaLabel = input<string | undefined>(undefined);

  /** Fires with the current file list on any change. */
  readonly filesChange = output<File[]>();
  /** Fires with rejected files and their reason. */
  readonly rejected = output<FileUploadRejection[]>();

  protected readonly files = signal<File[]>([]);
  protected readonly isDragOver = signal<boolean>(false);

  @ViewChild('picker') private picker?: ElementRef<HTMLInputElement>;

  private onChange: (v: File[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: File[] | null): void {
    this.files.set(value ?? []);
  }

  registerOnChange(fn: (v: File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    // Minimal wrapper; consumers disable via wrapping.
  }

  openPicker(): void {
    this.picker?.nativeElement.click();
  }

  onPicked(event: Event): void {
    const list = (event.target as HTMLInputElement).files;
    if (!list) return;
    this.addFiles(Array.from(list));
    // Reset the picker so the same file can be re-selected after removal.
    (event.target as HTMLInputElement).value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(): void {
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    const list = event.dataTransfer?.files;
    if (!list) return;
    this.addFiles(Array.from(list));
  }

  remove(file: File): void {
    this.files.update(arr => arr.filter(f => f !== file));
    const next = this.files();
    this.onChange(next);
    this.filesChange.emit(next);
    this.onTouched();
  }

  private addFiles(incoming: File[]): void {
    const rejections: FileUploadRejection[] = [];
    const accepted: File[] = [];
    const acceptAttr = this.accept().trim();

    for (const file of incoming) {
      if (acceptAttr && !this.fileMatchesAccept(file, acceptAttr)) {
        rejections.push({ file, reason: 'type', message: `File type not allowed: ${file.name}` });
        continue;
      }
      if (file.size > this.maxSizeBytes()) {
        rejections.push({ file, reason: 'size', message: `File too large: ${file.name}` });
        continue;
      }
      accepted.push(file);
    }

    let next = this.multiple() ? [...this.files(), ...accepted] : accepted.slice(0, 1);
    if (this.multiple() && next.length > this.maxFiles()) {
      const overflow = next.slice(this.maxFiles());
      overflow.forEach(f => rejections.push({ file: f, reason: 'count', message: `Max ${this.maxFiles()} files` }));
      next = next.slice(0, this.maxFiles());
    }

    this.files.set(next);
    this.onChange(next);
    this.filesChange.emit(next);
    this.onTouched();

    if (rejections.length) this.rejected.emit(rejections);
  }

  protected formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private fileMatchesAccept(file: File, accept: string): boolean {
    const entries = accept
      .split(',')
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);
    if (entries.length === 0) return true;
    const name = file.name.toLowerCase();
    const type = file.type.toLowerCase();
    return entries.some(entry => {
      if (entry.startsWith('.')) return name.endsWith(entry);
      if (entry.endsWith('/*')) return type.startsWith(entry.slice(0, -1));
      return type === entry;
    });
  }
}
